import { useEffect, type RefObject } from 'react';
import { useBlocker } from 'react-router-dom';
import type { EventBus } from '@servicetitan/web-components';
import { requestLeave } from './leaveHandshake';

interface CustomerMfeLeaveGuardProps {
  busRef: RefObject<EventBus | null>;
  /** Latest `navigation-guard` state from customer-mfe — true while Book Job is open. */
  guarded: boolean;
}

/**
 * Host half of customer-mfe's Book Job leave-guard handshake (REQ-JB-CMFE-022, CCPE-2172).
 * While the MFE reports `guarded`, a CCP route change is held and the MFE is asked for
 * permission via `request-leave`; it shows the same discard confirmation as its own
 * Back/close exits and answers with `leave-decision`, which proceeds or cancels the
 * navigation.
 *
 * Rendered by at most one mounted adapter at a time (the visible one) — a data router
 * honours only a single `useBlocker`.
 */
const CustomerMfeLeaveGuard = ({
  busRef,
  guarded,
}: CustomerMfeLeaveGuardProps) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      guarded && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      return;
    }
    const bus = busRef.current;
    if (!bus) {
      // No bus means no mounted MFE, so there is no booking left to protect.
      blocker.proceed();
      return;
    }
    return requestLeave(bus, {
      onProceed: () => blocker.proceed(),
      onAbort: () => blocker.reset(),
    });
  }, [blocker, busRef]);

  return null;
};

export default CustomerMfeLeaveGuard;
